import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace';
import { useValidatorsStore } from '@/stores/validators';
import { toast } from 'sonner';
import { FileNode } from '@/pages/workspace/types';
import { measure } from '@/lib/performance';

export const useWorkspaceActions = () => {
  const navigate = useNavigate();
  const { buildTestPromptAndAct, fixFailingTests, pasteToTask } = useWorkspaceStore((state) => ({
    buildTestPromptAndAct: state.buildTestPromptAndAct,
    fixFailingTests: state.fixFailingTests,
    pasteToTask: state.pasteToTask,
  }));
  const { run: runValidators } = useValidatorsStore((state) => ({
    run: state.run,
  }));

  const handleImproveTests = useCallback(measure(async (node: FileNode, getAllDescendantFiles: (node: FileNode, files: Set<string>) => void) => {
    const filesToTest: string[] = [];
    if (node.type === 'file') {
      filesToTest.push(node.path);
    } else {
      const files = new Set<string>();
      getAllDescendantFiles(node, files);
      filesToTest.push(...Array.from(files));
    }

    if (filesToTest.length > 0) {
      const result = await buildTestPromptAndAct(filesToTest);
      if (result?.taskId) {
        const interactiveQuery = result.interactive ? '&interactive=true' : '';
        navigate(`/pages/generation-progress/?taskId=${result.taskId}&proposalId=${result.proposalId}&model=${result.model}${interactiveQuery}`);
      }
    } else {
      toast.info('No tokenizable files found to improve tests for.');
    }
  }, 'handleImproveTests'), [buildTestPromptAndAct, navigate]);

  const handleRunTests = useCallback(measure(async (node: FileNode) => {
    const filesToTest: string[] = [];
    if (node.type === 'file') {
      filesToTest.push(node.path);
    } else {
      const allFiles = new Set<string>();
      const collectFiles = (n: FileNode) => {
        if (n.type === 'file') {
          allFiles.add(n.path);
        }
        if (n.children) {
          n.children.forEach(collectFiles);
        }
      };
      collectFiles(node);
      filesToTest.push(...Array.from(allFiles));
    }

    if (filesToTest.length === 0) {
      toast.info(`No files to test in ${node.path}`);
      return;
    }

    toast.info(`Running tests for ${node.path}...`);
    const runId = await runValidators({
      scope: 'selection',
      files: filesToTest,
    });
    if (runId) {
      navigate(`/pages/validators/?runId=${runId}`);
    }
  }, 'handleRunTests'), [runValidators, navigate]);

  const handleFixTests = useCallback(measure(async (node: FileNode) => {
    if (!node.testSummary?.runId || !node.testSummary.failing) {
      toast.info('No failing tests with a valid run ID found for this file.');
      return;
    }

    const result = await fixFailingTests({
      testFilePath: node.path,
      runId: node.testSummary.runId,
    });

    if (result?.taskId && result.model) {
      navigate(`/pages/generation-progress/?taskId=${result.taskId}&model=${result.model}`);
    }
  }, 'handleFixTests'), [fixFailingTests, navigate]);

  return {
    handleImproveTests,
    handleRunTests,
    handleFixTests,
    pasteToTask,
  };
};